import * as React from 'react'

import { i18n, useSiteConfig } from '@thg-commerce/enterprise-core'

import {
  Container,
  CountdownTitle,
  CutOffTime,
  TimeToCountdownContainer,
  TimeToCountdownText,
} from './styles'

const getCountdownTime = (countDown: number) => {
  const hours = Math.floor((countDown / 3600 / 1000) % 24)
  const minutes = Math.floor((countDown / 60 / 1000) % 60)
  const seconds = Math.floor((countDown / 1000) % 60)

  return { hours, minutes, seconds }
}

const useCountdown = (targetDate: number) => {
  const countDownDate = new Date(targetDate).getTime()

  const [countDown, setCountDown] = React.useState(
    countDownDate - new Date().getTime(),
  )

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCountDown(countDownDate - new Date().getTime())
    }, 1000)

    return () => clearInterval(interval)
  }, [countDownDate])

  return getCountdownTime(countDown)
}

function parseTimeParts(timeArray) {
  if (!timeArray) {
    return { hour: NaN, minute: NaN }
  }
  return {
    hour: parseInt(timeArray[1], 10),
    minute: parseInt(timeArray[2], 10),
  }
}

export const NextDayDeliveryCountdown = () => {
  const {
    enablePdpNextDayDeliveryCountDown,
    pdpNextDayDeliveryCountDownTo,
  } = useSiteConfig()

  const i18nText = {
    deliveryMessage: i18n('product.delivery.timer.message'),
    cutOffTime: i18n('product.delivery.cutoff.time'),
    deliveryMessage1: i18n('product.delivery.timer.message1'),
    durationHours: i18n('countdown.duration.hours'),
    durationMinutes: i18n('countdown.duration.minutes'),
    durationSeconds: i18n('countdown.duration.seconds'),
  }

  const hoursAndMinutes = /^(\d|\d\d):(\d\d)$/.exec(
    pdpNextDayDeliveryCountDownTo ?? '',
  )
  const { hour: targetHour, minute: targetMinute } = parseTimeParts(
    hoursAndMinutes,
  )

  const timeToCountdown =
    !isNaN(targetHour) && !isNaN(targetMinute)
      ? new Date().setHours(
          targetHour === 0 ? 24 : targetHour,
          targetMinute,
          0,
          0,
        )
      : null

  const { hours, minutes, seconds } = useCountdown(timeToCountdown ?? 0)

  const isEligibleForNextDay = () =>
    enablePdpNextDayDeliveryCountDown && pdpNextDayDeliveryCountDownTo

  const hasValidTimeParts = () =>
    Boolean(hoursAndMinutes) && !isNaN(targetHour) && !isNaN(targetMinute)

  const isTargetTimeInRange = () =>
    targetHour >= 0 &&
    targetHour <= 23 &&
    targetMinute >= 0 &&
    targetMinute <= 59

  const isValidTimeFormat = () => hasValidTimeParts() && isTargetTimeInRange()

  const isCountdownTimeValid = () =>
    timeToCountdown !== null && hours >= 0 && minutes >= 0 && seconds >= 0

  const isValid =
    isEligibleForNextDay() && isValidTimeFormat() && isCountdownTimeValid()

  if (!isValid) {
    return null
  }

  return (
    <Container>
      <CountdownTitle>
        {i18nText.deliveryMessage}
        <CutOffTime>{i18nText.cutOffTime}</CutOffTime>
        {i18nText.deliveryMessage1}
      </CountdownTitle>
      <TimeToCountdownContainer>
        <TimeToCountdownText>{hours}</TimeToCountdownText>
        {i18nText.durationHours}
        <TimeToCountdownText>{minutes}</TimeToCountdownText>
        {i18nText.durationMinutes}
        <TimeToCountdownText>{seconds}</TimeToCountdownText>
        {i18nText.durationSeconds}
      </TimeToCountdownContainer>
    </Container>
  )
}
